import { useState, useCallback, useEffect } from 'react';

interface LoadingStep {
  id: string;
  text: string;
  priority: number; // Чем меньше число, тем выше приоритет
}

class GlobalLoadingManager {
  private steps: Map<string, LoadingStep> = new Map();
  private listeners: Set<() => void> = new Set();

  addStep(id: string, text: string, priority: number = 10) {
    this.steps.set(id, { id, text, priority });
    this.notifyListeners();
  }

  removeStep(id: string) {
    this.steps.delete(id);
    this.notifyListeners();
  }

  updateStep(id: string, text: string) {
    const step = this.steps.get(id);
    if (step) {
      step.text = text;
      this.notifyListeners();
    }
  }

  getActiveSteps(): LoadingStep[] {
    return Array.from(this.steps.values()).sort((a, b) => a.priority - b.priority);
  }

  hasActiveSteps(): boolean {
    return this.steps.size > 0;
  }

  subscribe(listener: () => void) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener());
  }

  clear() {
    this.steps.clear();
    this.notifyListeners();
  }
}

// Глобальный экземпляр менеджера
const globalLoadingManager = new GlobalLoadingManager();

export const useGlobalLoading = () => {
  const [, forceUpdate] = useState({});

  const refresh = useCallback(() => {
    forceUpdate({});
  }, []);

  // Подписываемся на изменения
  useEffect(() => {
    const unsubscribe = globalLoadingManager.subscribe(refresh);
    return unsubscribe;
  }, [refresh]);

  const addLoadingStep = useCallback((id: string, text: string, priority: number = 10) => {
    globalLoadingManager.addStep(id, text, priority);
  }, []);

  const removeLoadingStep = useCallback((id: string) => {
    globalLoadingManager.removeStep(id);
  }, []);

  const updateLoadingStep = useCallback((id: string, text: string) => {
    globalLoadingManager.updateStep(id, text);
  }, []);

  const clearAllSteps = useCallback(() => {
    globalLoadingManager.clear();
  }, []);

  return {
    activeSteps: globalLoadingManager.getActiveSteps(),
    hasActiveSteps: globalLoadingManager.hasActiveSteps(),
    addLoadingStep,
    removeLoadingStep,
    updateLoadingStep,
    clearAllSteps
  };
}; 